import { fmt, withUnit } from './format';

// Vehicle header: VIN, brand, then the fields the documents hold; a field without a value is left out
export const buildVehicle = vehicle => {
    if (!vehicle) return null;

    return {
        vin: vehicle.vin,
        brand: vehicle.brand,
        hint: vehicle.hint || '',
        fields: (vehicle.fields || [])
            .filter(f => f.value != null && f.value !== '')
            .map(f => ({ key: f.key, label: f.label, value: String(f.value), sub: f.sub || '' })),
    };
};

// Stat tiles from the visit stats; a tile whose value is unknown is left out. Explicit tiles replace them.
export const buildTiles = (report, t) => {
    if (report.tiles) {
        return report.tiles.map(tile => ({ ...tile, href: tile.href || '#' }));
    }

    const stats = report.stats || {};
    const tiles = [];
    if (stats.visits != null)
        tiles.push({
            value: String(stats.visits),
            label: t('tiles.visits', { count: stats.visits }),
            href: '#timeline',
        });
    if (stats.period) tiles.push({ value: stats.period, label: t('tiles.period'), href: '#timeline', small: true });
    if (stats.lastKm != null) {
        const label = stats.lastDate ? t('tiles.lastReadingOn', { date: stats.lastDate }) : t('tiles.lastReading');
        tiles.push({ value: withUnit(fmt(stats.lastKm), 'km'), label, href: '#mileage' });
    }
    if (stats.components != null)
        tiles.push({ value: String(stats.components), label: t('tiles.components'), href: '#history' });
    if (stats.redFlags != null) {
        tiles.push({
            value: String(stats.redFlags),
            label: t('tiles.redFlags'),
            href: '#ai',
            alert: stats.redFlags > 0,
        });
    }

    return tiles;
};

// AI overview. "No red flags" is said only when the flags were actually assessed (an array, even empty).
export const buildAi = ai => {
    if (!ai) return null;

    const ok = ai.status !== 'unavailable';
    const flags = ok ? (ai.flags || []).map(f => ({ title: f.title, reason: f.reason || '', when: f.when || '' })) : [];

    return {
        ok,
        unavailable: !ok,
        paragraphs: ok ? ai.paragraphs || [] : [],
        flags,
        noFlags: ok && Array.isArray(ai.flags) && flags.length === 0,
        // 'inspection': written from our inspection alone while the service history is read (SC-QA-61); on an
        // unavailable overview it means the inspection one could not be made, while the service history may still come
        basis: ai.basis || null,
    };
};

export const buildStatus = status =>
    (status || []).map(card => ({ ...card, tone: card.tone || 'neutral', sub: card.sub || '' }));

export const buildNotices = notices =>
    (notices || []).map(n => ({
        title: n.title,
        text: n.text || '',
        link: n.link || '',
        href: n.href || '#original',
        tone: n.tone || 'info',
        // its text is for the screen only ("this page updates by itself"): the PDF prints the title (SC-QA-72)
        screenOnlyText: !!n.screenOnlyText,
        // a button such as Retry on a translation notice (SC-QA-119): { label, onClick }
        action: n.action || null,
    }));

// Document info: next due dates from the service document, type approval, extra fields, files used
export const buildDocInfo = (docinfo, t) => {
    const d = docinfo || {};
    const fields = [];
    if (d.main) fields.push({ label: t('docinfo.nextInspection'), value: d.main, sub: t('docinfo.perDocument') });
    if (d.emissions)
        fields.push({ label: t('docinfo.nextEmissions'), value: d.emissions, sub: t('docinfo.perDocument') });
    if (d.approval) fields.push({ label: t('docinfo.approval'), value: d.approval, mono: true });
    (d.extra || []).forEach(x => fields.push({ label: x.label, value: x.value, sub: x.sub || '', mono: !!x.mono }));

    return { fields, files: d.files || [] };
};

// "Volkswagen Polo": the model prefixed with the brand unless it already starts with it; the brand alone without one
const modelWithBrand = (model, brand) => {
    if (!model) return brand;
    return model.value.startsWith(brand) ? model.value : `${brand} ${model.value}`;
};

// The car the history belongs to: model with brand, then engine, gearbox and body from the vehicle fields
export const buildHistoryCar = (report, t) => {
    const vehicle = report.vehicle;
    if (!vehicle) return null;

    // fields are found by key (labels are translated)
    const field = keys => (vehicle.fields || []).find(f => keys.includes(f.key) && f.value) || null;
    const model = field(['model']);
    const engine = field(['engine', 'engineCode']);
    const engineSize = field(['engineSize']);
    const gearbox = field(['gearbox', 'transmission']);
    const body = field(['bodyType']);
    const brand = vehicle.brand || '';

    const spec = [
        model && model.sub,
        engine && engine.value + (engine.sub ? ` ${engine.sub}` : ''),
        engineSize && engineSize.value,
        gearbox && gearbox.value + (gearbox.sub ? `, ${gearbox.sub}` : ''),
        body && body.value,
    ].filter(Boolean);

    const stats = report.stats || {};
    const period = [stats.visits != null && t('history.visitCount', { count: stats.visits }), stats.period].filter(
        Boolean
    );

    return {
        brand,
        model: modelWithBrand(model, brand),
        spec: spec.join(' · '),
        period: period.join(' · '),
    };
};
